import { faDiamondExclamation, faIdCard, faInfoCircle, faUserSlash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Masonry } from "@mui/lab"
import {
  Autocomplete,
  BottomNavigation,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormLabel,
  Grid,
  List,
  ListItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Stack,
  CircularProgress,
  useTheme
} from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import CompanyDepartmentSelection from "components/CompanyDepartmentSelection/CompanyDepartmentSelection"
import PaperBox from "components/PaperBox"
import { Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import {
  userCreateValidationSchema,
  userValidationSchema
} from "pages/systemadmin/pages/customers/components/validationSchema"
import { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"
import { StyledFormControlLabel, StyledPrimaryAvatar } from "shared/styled/Global"
import { BplUserInfo } from "./BplUserInfo"
import RoleTemplatesForm from "./RoleTemplates/RoleTemplates"
import { UsernameField } from "./UsernameField"
import { deviationType } from "shared/interfaces/deviation"
import { StandardTextBox } from "components/StandardTextBox/StandardTextBox"

const isFeatureInGroup = (group, featureId) => {
  return group.features.findIndex((feature) => feature.featureId === featureId && feature.enabled) > -1
}

const findGroupInModule = (module, featureId) => {
  return module.groups.findIndex((group) => isFeatureInGroup(group, featureId)) > -1
}

export const getFeaturePath = (modules, featureId) => {
  const moduleIndex = modules?.findIndex((module) => findGroupInModule(module, featureId))
  if (moduleIndex === -1) return null

  const groupIndex = modules[moduleIndex]?.groups?.findIndex((group) => isFeatureInGroup(group, featureId))
  if (groupIndex === -1) return null

  const featureIndex = modules[moduleIndex]?.groups[groupIndex]?.features?.findIndex(
    (f) => f.featureId === featureId
  )
  if (featureIndex === -1) return null

  return `modules.${moduleIndex}.groups.${groupIndex}.features.${featureIndex}`
}

export const findFeatureInModules = (modules, featureId) => {
  return modules
    .flatMap((module) => module.groups)
    .flatMap((group) => group.features)
    .find((feature) => feature.featureId === featureId)
}

const getCombinedValidationSchema = (validationSchemaArg, retriggerValidationObjArg, t) => {
  let combinedValidationSchema = validationSchemaArg
  if (retriggerValidationObjArg.planningActive && retriggerValidationObjArg.isBplUrlValid) {
    const bplInfoValidationObj = {
      bplInfo: Yup.object().shape({
        stationLocationId: Yup.string()
          .nullable()
          .required(t("settings.user.validation.station_required", "Station location is required")),
        employeeCode: Yup.string()
          .nullable()
          .required(
            t("settings.user.validation.type_of_employment_is_required", "Type of employment is required")
          ),
        agreementCode: Yup.string()
          .nullable()
          .required(t("settings.user.validation.workingschedule_required", "Working schedule is required")),
        workingScheduleCode: Yup.string()
          .nullable()
          .required(t("settings.user.validation.agreement_required", "Agreement is required")),
        workingPercentTime: Yup.number()
          .min(0, t("settings.user.validation.cannot_be_lower_than_zero", "Cannot be lower than 0"))
          .max(100, t("settings.user.validation.cannot_be_higher_than_100", "Cannot be higher than 100")),
        managerId: Yup.string()
          .nullable()
          .required(t("settings.user.validation.manager_is_required", "Manager is required"))
      })
    }
    combinedValidationSchema = combinedValidationSchema.concat(Yup.object().shape(bplInfoValidationObj))
  } else {
    const bplInfoValidationObj = {
      bplInfo: Yup.object().shape({
        stationLocationId: Yup.string().nullable(),
        employeeCode: Yup.string().nullable(),
        agreementCode: Yup.string().nullable(),
        workingScheduleCode: Yup.string().nullable(),
        workingPercentTime: Yup.number().nullable(),
        managerId: Yup.string().nullable()
      })
    }
    combinedValidationSchema = combinedValidationSchema.concat(Yup.object().shape(bplInfoValidationObj))
  }

  if (retriggerValidationObjArg.violationActive) {
    const violationObj = {
      driverCardNumber: Yup.string()
        .nullable()
        .required(t("settings.user.driver_license_is_required", "Driver's license is required"))
    }
    combinedValidationSchema = combinedValidationSchema.concat(Yup.object().shape(violationObj))
  } else {
    const violationObj = { driverCardNumber: Yup.string().nullable() }
    combinedValidationSchema = combinedValidationSchema.concat(Yup.object().shape(violationObj))
  }

  if (retriggerValidationObjArg.bkyChecksActive) {
    const bkyChecksObj = {
      socialSecurityNumber: Yup.string()
        .matches(
          /^\d{12}$/,
          t("settings.user.social_nr_must_contain", "Social security number must contain 12 digits")
        )
        .required(
          t(
            "settings.user.personal_identification number_is_required",
            "Personal identification number is required"
          )
        ),
      drivingLicensQualifications: Yup.array()
        .of(Yup.string())
        .min(1, t("settings.user.driving_license_is_required", "Driving license is required"))
        .required(t("settings.user.driving_license_is_required", "Driving license is required"))
    }
    combinedValidationSchema = combinedValidationSchema.concat(Yup.object().shape(bkyChecksObj))
  } else {
    const bkyChecksObj = {
      socialSecurityNumber: Yup.number().nullable(),
      drivingLicensQualifications: Yup.array().of(Yup.string()).nullable()
    }
    combinedValidationSchema = combinedValidationSchema.concat(Yup.object().shape(bkyChecksObj))
  }

  return combinedValidationSchema
}

const addAllFeatures = (featureGroupIndex, enabled, groups, setFieldValue, appContext, customerEnabledFeatures) => {
  setFieldValue(`modules.${featureGroupIndex}.enabled`, !enabled)
  groups?.map((feature, groupIndex) => {
    setFieldValue(`modules.${featureGroupIndex}.groups.${groupIndex}.enabled`, !enabled)
    return feature.features?.map((feature, featureIndex) => {
      return setFieldValue(
        `modules.${featureGroupIndex}.groups.${groupIndex}.features.${featureIndex}.enabled`,
        !enabled
          ? (hasRole(appContext.appUser, allRoles.CustomerAdmin) || hasRole(appContext.appUser, feature.roleId))
            && customerEnabledFeatures?.includes(feature.featureId)
          : false
      )
    })
  })
}

const initialValidationObj = {
  planningActive: null,
  violationActive: null,
  bkyChecksActive: null,
  isBplUrlValid: null
}

const bplInfoEmptyData = {
  id: null,
  reconciliationPeriodMonth: 6,
  timebankMaxHours: 50,
  timebankMinHours: -50,
  workingWeeklyTime: 40,
  workingPercentTime: 100,
  agreementCode: null,
  stationLocationId: null,
  roles: [],
  workingScheduleCode: null,
  employeeCode: null,
  managerId: null,
  externalSync: false
}

export const UserForm = ({
  passwordSuggestEndpoint,
  customer,
  onSubmit,
  initialValues,
  result,
  feedback,
  parentIsSysAdmin,
  addUser
}) => {
  const formRef = useRef()
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const [roleResult, listRoleApi] = useApi()

  const drivingLicensQualifications = [
    {
      key: "buss",
      label: t("global.bus", "Bus"),
      qualifications: ["DE", "D", "D1E", "YKBBuss", "D1"]
    },
    {
      key: "taxi",
      label: t("global.taxi", "Taxi"),
      qualifications: [t("global.taxi_leg", "TaxiLeg")]
    },
    {
      key: "gods",
      label: t("global.freight", "Freight"),
      qualifications: ["B", "BE", "CE", "C", "C1E", "C1"]
    }
  ]

  const theme = useTheme()
  const [retriggerValidationObj, setRetriggerValidationObj] = useState(initialValidationObj)
  const [bplOptionsResult, bplOptionsApi] = useApi()
  const [bplUserResult, bplUserApi] = useApi()
  const [userTemplateResult, userTemplateApi] = useApi()
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialValues?.mainCompanyId)
  const [searchResult, searchApi] = useApi()
  const [validationStep, setValidationStep] = useState(
    addUser ? userCreateValidationSchema(t) : userValidationSchema(t)
  )
  const isLimitedManager =
    !parentIsSysAdmin && hasRole(appContext.appUser, allRoles.CustomerAdminLimitedManagerAccess)
  const [useRoleTemplate, setUseRoleTemplate] = useState(initialValues.useRoleTemplates)

  const retriggerValidation = (retriggerValidationObjArg) => {
    const combinedValidationSchema = getCombinedValidationSchema(validationStep, retriggerValidationObjArg, t)
    setValidationStep(combinedValidationSchema)
  }

  const handleSubmit = async (values, { resetForm, errors }) => {
    const customerId = customer.id

    const userToCredate = {
      ...values,
      password: window.btoa(values.password),
      customerId: customerId
    }
    if (!findFeatureInModules(values?.modules, "Planning")?.enabled) {
      userToCredate.bplInfo = bplInfoEmptyData
    }
    onSubmit(userToCredate)
  }

  useEffect(() => {
    userTemplateApi.get(apiEndpoints.customeradmin.getuserrolestemplates.replace("{customerId}", customer.id))
  }, [])

  useEffect(() => {
    const bplUserUrl = `${apiEndpoints.customeradmin.getbpluserbyid.replace(
      "{id}",
      initialValues.id
    )}?companyId=${selectedCompanyId}`
    bplUserApi.get(bplUserUrl)
    bplOptionsApi.get(apiEndpoints.customeradmin.getbpltypes.replace("{companyId}", selectedCompanyId))
  }, [selectedCompanyId])

  useEffect(() => {
    setRetriggerValidationObj((prevState) => ({
      ...prevState,
      isBplUrlValid: bplOptionsResult.status === 2
    }))
  }, [bplOptionsResult])

  useEffect(() => {
    parentIsSysAdmin
      ? listRoleApi.get(
          apiEndpoints.systemadmincustomers.getcustomercustomroles.replace("{customerid}", customer.id)
        )
      : listRoleApi.get(apiEndpoints.customeradmin.getcustomercustomroles)
  }, [])

  useEffect(() => {
    searchApi.get(
      !addUser && parentIsSysAdmin
        ? apiEndpoints.systemadmincustomers.getusersselectlistformanagerdropdownedit
            .replace("{id}", customer.id)
            .replace("{userId}", initialValues.id)
        : !addUser && !parentIsSysAdmin
        ? apiEndpoints.customeradmin.getusersselectlistformanagerdropdownedit.replace(
            "{userId}",
            initialValues.id
          )
        : addUser && parentIsSysAdmin
        ? apiEndpoints.systemadmincustomers.getusersselectlistformanagerdropdown.replace("{id}", customer.id)
        : addUser && !parentIsSysAdmin && isLimitedManager
        ? apiEndpoints.customeradmin.gethierarchymanagers
        : addUser && !parentIsSysAdmin
        ? apiEndpoints.customeradmin.getusersselectlistformanagerdropdown
        : ""
    )
  }, [])

  useEffect(() => {
    if (!useRoleTemplate) {
      const planningActive = findFeatureInModules(initialValues?.modules, "Planning")?.enabled
      const isBplUrlValid = bplOptionsResult.status === 2
      const violationActive = findFeatureInModules(initialValues?.modules, "Violation")?.enabled

      setRetriggerValidationObj((prevState) => ({
        ...prevState,
        planningActive,
        violationActive,
        isBplUrlValid
      }))
    }
  }, [])

  useEffect(() => {
    retriggerValidation(retriggerValidationObj)
  }, [retriggerValidationObj])

  return initialValues ? (
    <Formik
      innerRef={formRef}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      enableReinitialize={true}
      validateOnBlur={true}
      validationSchema={validationStep}>
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        handleSubmit,
        setFieldValue,
        validateField,
        setFieldTouched
      }) => (
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2} mt={0}>
            <Grid xs={12} lg={8} pl={2}>
              <PaperBox mt={0}>
                <Grid container spacing={2}>
                  {addUser && (
                    <UsernameField
                      values={values}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      touched={touched}
                      errors={errors}
                      passwordSuggestEndpoint={passwordSuggestEndpoint}
                    />
                  )}
                  {!addUser && (
                    <>
                      <Grid item xs={12} md={8}>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          id={"username"}
                          label={t(`settings.user.username`, ``)}
                          value={values.username}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.username && errors.username ? true : false}
                          helperText={touched.username && errors.username ? errors.username : null}
                          variant="outlined"
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={4}>
                      {searchResult.status === 2 ? (
                        <Autocomplete
                          style={{
                            width: "100%"
                          }}
                          disabled={searchResult.status !== 2}
                          componentsProps={{ popper: { style: { width: "240px" } } }}
                          id="manager.id"
                          options={
                            searchResult.data.sort((a, b) => a.name.localeCompare(b.name))
                          }
                          renderOption={(props, option) =>
                            <li {...props}>
                              <Stack direction="row" spacing={2} justifyContent="space-between">
                                <Tooltip title="disabled">
                                  <Box sx={{ fontWeight: 400, minWidth: "100px" }}>{option?.name}</Box>
                                  {option.isManagerCircularManager && (
                                    <Box sx={{ opacity: 0.6 }}>{t("global.already_manager", "You are already manager")}</Box>
                                  )}
                                </Tooltip>
                              </Stack>
                            </li>
                          }
                          getOptionDisabled={option => option.isManagerCircularManager}
                          getOptionLabel={(option) => (option.name ? option.name : "")}
                          value={
                            (searchResult.status === 2 &&
                              searchResult.data.find((i) => i.id === values.manager?.id)) ||
                            ""
                          }
                          isOptionEqualToValue={(option, value) => {
                            return option.id === value.id
                          }}
                          onChange={(e, value) => {
                            if (!value?.id) {
                              setFieldValue("manager", null)
                              return
                            }
                            setFieldValue("manager.id", value?.id)
                            setFieldValue("manager.bplUserId", value?.bplUserId)
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label={t(`settings.user.manager`, ``)}
                              name="manager.id"
                            />
                          )}
                        />
                      ) : searchResult.status === 0 || searchResult.status === 1 ? (
                        <Autocomplete
                          style={{
                            width: "100%"
                          }}
                          disabled={true}
                          value={values?.manager?.name}
                          options={[]}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label={t(`settings.user.manager`, ``)}
                              name="manager.id"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: <CircularProgress color="inherit" size={20} />
                              }}
                            />
                          )}
                        />
                      ) : (<Typography>Error getting data</Typography>)}
                    </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="name"
                      label={t(`settings.user.name`, ``)}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && errors.name ? true : false}
                      helperText={touched.name && errors.name ? errors.name : null}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="employeeId"
                      label={t(`settings.user.employeeId`, ``)}
                      value={values.employeeId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.employeeId && errors.employeeId ? true : false}
                      helperText={touched.employeeId && errors.employeeId ? errors.employeeId : null}
                      variant="outlined"
                      disabled={
                        !hasRole(appContext.appUser, allRoles.CustomerAdmin) ||
                        (hasRole(appContext.appUser, allRoles.CustomerAdmin) &&
                          hasRole(appContext.appUser, allRoles.CustomerAdminLimitedManagerAccess))
                      }
                    />
                  </Grid>
                  {!addUser && (
                    <Grid item xs={12} md={8}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id={"email"}
                        label={t(`settings.user.email`, ``)}
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && errors.email ? true : false}
                        helperText={touched.email && errors.email ? errors.email : null}
                        variant="outlined"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="phone"
                      label={t(`settings.user.phone`, ``)}
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone && errors.phone ? true : false}
                      helperText={touched.phone && errors.phone ? errors.phone : null}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="streetAddress"
                      label={t(`settings.form.streetAddress`, `Street address`)}
                      value={values.streetAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.streetAddress && errors.streetAddress ? true : false}
                      helperText={touched.streetAddress && errors.streetAddress ? errors.streetAddress : null}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="postalCode"
                      label={t(`settings.form.postalCode`, `Postal code`)}
                      value={values.postalCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.postalCode && errors.postalCode ? true : false}
                      helperText={touched.postalCode && errors.postalCode ? errors.postalCode : null}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="cityAddress"
                      label={t(`settings.form.city`, `City`)}
                      value={values.cityAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.cityAddress && errors.cityAddress ? true : false}
                      helperText={touched.cityAddress && errors.cityAddress ? errors.cityAddress : null}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="socialSecurityNumber"
                      label={t(`settings.user.socialsecuritynumber`, `Social security number`)}
                      value={values.socialSecurityNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      InputLabelProps={{ shrink: values.socialSecurityNumber ? true : undefined }}
                      error={touched.socialSecurityNumber && errors.socialSecurityNumber ? true : false}
                      helperText={
                        touched.socialSecurityNumber && errors.socialSecurityNumber
                          ? errors.socialSecurityNumber
                          : null
                      }
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={(event) => {
                            setFieldValue("isManager", event.target.checked)
                          }}
                          checked={values.isManager}
                        />
                      }
                      label={t("global.manager", "Manager")}
                    />
                    <FormLabel sx={{ ml: "4px" }}>
                      {t(
                        "settings.user.could_be_choosen_as_manager",
                        "(May be elected as the immediate superior of others)"
                      )}
                    </FormLabel>
                  </Grid>
                </Grid>
              </PaperBox>
              <PaperBox>
                {userTemplateResult.status === 2 && userTemplateResult.data.length > 0 && (
                  <Grid item xs={12} display="flex" alignItems="center" mb={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={() => {
                            setUseRoleTemplate(!useRoleTemplate)
                            setFieldValue("useRoleTemplates", !useRoleTemplate)
                          }}
                          checked={useRoleTemplate}
                        />
                      }
                      label={t("settings.user.use_role_template", "use role templates")}
                    />
                  </Grid>
                )}

                {useRoleTemplate && userTemplateResult.status === 2 ? (
                  <RoleTemplatesForm
                    setFieldValue={setFieldValue}
                    selectedTemplateIds={values.userRoleTemplateIds}
                    userRoleTemplates={userTemplateResult.data}
                    setRetriggerValidationObj={setRetriggerValidationObj}
                    formValues={values}
                  />
                ) : (
                  <Masonry columns={{ xs: 1, md: 2 }} spacing={3}>
                    {values.modules?.map((category, moduleIndex) => {
                      return (
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h5">
                              {t(`settings.user.modules.${category.translateId}`, category.translateId)}
                            </Typography>
                            {category?.groups?.reduce(
                              (total, group) => total + (group.features?.length || 0),
                              0
                            ) > 1 &&
                              category?.translateId !== "settings" && (
                                <StyledFormControlLabel
                                  label={t(`settings.user.modules.choose_all`)}
                                  control={
                                    <Checkbox
                                      onChange={() => {
                                        addAllFeatures(
                                          moduleIndex,
                                          category?.groups.every((group) =>
                                            group.features
                                              .filter(
                                                (feature) =>
                                                  (hasRole(appContext.appUser, allRoles.CustomerAdmin) ||
                                                    hasRole(appContext.appUser, feature.roleId)) &&
                                                  customer.enabledFeatures?.includes(feature.featureId)
                                              )
                                              .every((feature) => feature.enabled)
                                          ),
                                          category?.groups,
                                          setFieldValue,
                                          appContext,
                                          customer.enabledFeatures
                                        )
                                      }}
                                      id={category.translateId}
                                      checked={category?.groups.every((group) =>
                                        group.features
                                          .filter(
                                            (feature) =>
                                              (hasRole(appContext.appUser, allRoles.CustomerAdmin) ||
                                                hasRole(appContext.appUser, feature.roleId)) &&
                                              customer.enabledFeatures?.includes(feature.featureId)
                                          )
                                          .every((feature) => feature.enabled)
                                      )}
                                      disabled={
                                        !hasRole(appContext.appUser, allRoles.CustomerAdmin) &&
                                        !category?.groups.every((group) =>
                                          group.features.every((feature) =>
                                            hasRole(appContext.appUser, feature.roleId)
                                          )
                                        )
                                      }
                                    />
                                  }
                                />
                              )}
                          </Box>

                          {category.groups?.map((group, groupIndex) => {
                            return (
                              <Box>
                                {group.translateId && (
                                  <Typography variant="caption" mt={1}>
                                    {t(`settings.user.modules.${group.translateId}`, group.translateId)}
                                  </Typography>
                                )}

                                {group.features?.map((feature, featureIndex) => {
                                  const isDeviationType = Object.values(deviationType).includes(
                                    feature.featureId
                                  )
                                  const customerIsMissing = !customer.enabledFeatures?.includes(
                                    feature.featureId
                                  )
                                  if (isDeviationType && customerIsMissing) return
                                  const isPlanning =
                                    feature.featureId === "Planning" && feature.roleId === "Planning"
                                  const mainCompanyName = customer.companies?.find(
                                    (comp) => comp.id === selectedCompanyId
                                  )?.name
                                  let helpText =
                                    bplOptionsResult.status !== 2
                                      ? " (" +
                                        t(`settings.user.bpl.missing_bpl_url`, "BPL is not activated on") +
                                        " " +
                                        mainCompanyName +
                                        ")"
                                      : ""
                                  if (!mainCompanyName) {
                                    helpText =
                                      " (" +
                                      t(
                                        `settings.user.bpl.choose_company`,
                                        "Choose a BPL supported company first"
                                      ) +
                                      ")"
                                  }
                                  if (isPlanning) {
                                    return (
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <FormControlLabel
                                          sx={{
                                            "& .MuiFormControlLabel-label": {
                                              mb: "4px"
                                            }
                                          }}
                                          control={
                                            <Checkbox
                                              onChange={(e) => {
                                                const newValue = e.target.checked
                                                setFieldValue(
                                                  `modules.${moduleIndex}.groups.${groupIndex}.features.${featureIndex}.enabled`,
                                                  newValue && bplOptionsResult.status === 2
                                                )

                                                if (isPlanning) {
                                                  setRetriggerValidationObj((prevState) => ({
                                                    ...prevState,
                                                    planningActive: newValue,
                                                    isBplUrlValid: bplOptionsResult.status === 2
                                                  }))
                                                }
                                              }}
                                              checked={bplOptionsResult.status === 2 && feature?.enabled}
                                              disabled={bplOptionsResult.status !== 2 || !mainCompanyName}
                                            />
                                          }
                                          label={
                                            feature.translateId &&
                                            t(
                                              `settings.user.modules.${feature.translateId}`,
                                              feature.translateId
                                            ) + helpText
                                          }
                                          disabled={
                                            (feature.featureId === "Planning" &&
                                              bplOptionsResult.status !== 2) ||
                                            !mainCompanyName
                                          }
                                        />
                                        {feature.infoTranslateId !== null && (
                                          <Tooltip
                                            title={t(
                                              `settings.user.modules.${feature.infoTranslateId}`,
                                              "InfoTranslateId"
                                            )}>
                                            <FontAwesomeIcon
                                              icon={faInfoCircle}
                                              color={theme.palette.primary[700]}
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                    )
                                  }
                                  return (
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <FormControlLabel
                                        sx={{
                                          "& .MuiFormControlLabel-label": {
                                            mb: "4px"
                                          }
                                        }}
                                        control={
                                          <Checkbox
                                            onChange={(e) => {
                                              const newValue = e.target.checked
                                              setFieldValue(
                                                `modules.${moduleIndex}.groups.${groupIndex}.features.${featureIndex}.enabled`,
                                                !feature?.enabled
                                              )

                                              if (
                                                feature.featureId === "Violation" &&
                                                feature.roleId === "Violation"
                                              ) {
                                                setRetriggerValidationObj((prevState) => ({
                                                  ...prevState,
                                                  violationActive: newValue
                                                }))
                                              } else if (isPlanning) {
                                                setRetriggerValidationObj((prevState) => ({
                                                  ...prevState,
                                                  planningActive: newValue
                                                }))
                                              } else if (
                                                feature.featureId === "BkyChecks" &&
                                                feature.roleId === "BkyDriver"
                                              ) {
                                                setRetriggerValidationObj((prevState) => ({
                                                  ...prevState,
                                                  bkyChecksActive: newValue
                                                }))
                                              }
                                            }}
                                            checked={feature?.enabled}
                                            disabled={
                                              !hasRole(appContext.appUser, allRoles.CustomerAdmin) &&
                                              !hasRole(appContext.appUser, feature.roleId)
                                            }
                                          />
                                        }
                                        label={
                                          feature.translateId &&
                                          t(
                                            `settings.user.modules.${feature.translateId}`,
                                            feature.translateId
                                          )
                                        }
                                      />
                                      {feature.infoTranslateId !== null && (
                                        <Tooltip
                                          title={t(
                                            `settings.user.modules.${feature.infoTranslateId}`,
                                            "InfoTranslateId"
                                          )}>
                                          <FontAwesomeIcon
                                            icon={faInfoCircle}
                                            color={theme.palette.primary[700]}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  )
                                })}
                              </Box>
                            )
                          })}

                          <Divider />
                        </Box>
                      )
                    })}
                  </Masonry>
                )}
              </PaperBox>
            </Grid>
            <Grid xs={12} lg={4} pl={2} pt={{ xs: 2, lg: 0 }}>
              {!addUser && (
                <PaperBox sx={{ mt: 0, mb: 2 }}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center">
                    <Grid item xs={2}>
                      <Box>
                        <StyledPrimaryAvatar
                          aria-label={t("settings.user.sync_user_to_bpl", "Sync user to BPL")}>
                          <FontAwesomeIcon icon={faUserSlash} size="lg" />
                        </StyledPrimaryAvatar>
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="h5">
                        {t(`settings.user.inactive_user`, `Inactive user`)}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={<Switch onChange={handleChange} id="disabled" checked={values.disabled} />}
                      />
                    </Grid>
                  </Grid>
                </PaperBox>
              )}
              <PaperBox sx={{ mt: 0 }}>
                <Typography variant="h5">{t("settings.form.roles", "Roles")}</Typography>
                {roleResult.status === 2 && (
                  <List>
                    {roleResult.data.length > 0 ? (
                      roleResult.data.map((role) => {
                        return (
                          <ListItem disablePadding>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  onChange={(e) => {
                                    const value = Number(e.target.value)
                                    const customRoleIds = values.customRoleIds ? values.customRoleIds : []

                                    if (!customRoleIds.includes(value)) {
                                      customRoleIds.push(value)
                                    } else {
                                      const index = customRoleIds.findIndex((x) => x === value)
                                      if (index > -1) {
                                        customRoleIds.splice(index, 1)
                                      }
                                    }

                                    setFieldValue && setFieldValue("customRoleIds", customRoleIds)
                                  }}
                                  id="customRoleIds"
                                  value={role.id}
                                  checked={
                                    values?.customRoleIds &&
                                    values?.customRoleIds?.findIndex((x) => x === role.id) !== -1
                                      ? true
                                      : false
                                  }
                                />
                              }
                              label={role.name}
                            />
                          </ListItem>
                        )
                      })
                    ) : (
                      <ListItem disablePadding>
                        <Typography variant="body2" color="textSecondary">
                          {t(`settings.user.no_roles_posted`, `No roles posted`)}
                        </Typography>
                      </ListItem>
                    )}
                  </List>
                )}
              </PaperBox>

              <PaperBox>
                <CompanyDepartmentSelection
                  companies={customer.companies}
                  initCompanies={initialValues.companyIds}
                  initDepartments={initialValues.departmentIds}
                  setSelectedCompanyId={setSelectedCompanyId}
                  values={values}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  touched={touched}
                  errors={errors}
                  mainCompanySelect={true}
                />
              </PaperBox>

              <PaperBox>
                <Grid
                  container
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom={4}>
                  <Grid item xs={2}>
                    <Box>
                      <StyledPrimaryAvatar
                        aria-label={t("settings.user.violation_integration", "Violations integration")}>
                        <FontAwesomeIcon icon={faDiamondExclamation} size="lg" />
                      </StyledPrimaryAvatar>
                    </Box>
                  </Grid>
                  <Grid item xs={10}>
                    <Typography variant="h5">
                      {t("settings.user.violation_integration", "Violations integration")}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    id="driverCardNumber"
                    label={t(`settings.user.drivercardnr`, ``)}
                    value={values.driverCardNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.driverCardNumber && errors.driverCardNumber ? true : false}
                    helperText={
                      touched.driverCardNumber && errors.driverCardNumber ? errors.driverCardNumber : null
                    }
                    variant="outlined"
                  />
                </Grid>
              </PaperBox>
              {(findFeatureInModules(values?.modules, "BkyChecks")?.enabled ||
                (userTemplateResult.status === 2 &&
                  userTemplateResult.data
                    .filter((template) => values?.UserRoleTemplateIds?.includes(template.id))
                    .some((template) => template.roles.some((role) => role.role.startsWith("Bky"))))) && (
                <PaperBox>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom={4}>
                    <Grid item xs={2}>
                      <Box>
                        <StyledPrimaryAvatar
                          aria-label={t("settings.user.bky_integration", "BKY controls integration")}>
                          <FontAwesomeIcon icon={faIdCard} size="lg" />
                        </StyledPrimaryAvatar>
                      </Box>
                    </Grid>
                    <Grid item xs={10}>
                      <Typography variant="h5">
                        {t("settings.user.bky_integration", "BKY controls integration")}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container column spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="socialSecurityNumber"
                        label={t(`settings.user.socialsecuritynumber`, `Social security number`)}
                        value={values.socialSecurityNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{ shrink: values.socialSecurityNumber ? true : undefined }}
                        error={touched.socialSecurityNumber && errors.socialSecurityNumber ? true : false}
                        helperText={
                          touched.socialSecurityNumber && errors.socialSecurityNumber
                            ? errors.socialSecurityNumber
                            : null
                        }
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        sx={{ width: "100%" }}
                        multiple
                        id="drivingLicensQualifications"
                        options={drivingLicensQualifications.flatMap((group) =>
                          group.qualifications.map((qualification) => ({
                            category: group.label,
                            label: qualification
                          }))
                        )}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(opt, val) => opt.label === val.label}
                        value={(values.drivingLicensQualifications || []).map((label) => {
                          const match = drivingLicensQualifications
                            .flatMap((group) =>
                              group.qualifications.map((qualification) => ({
                                category: group.label,
                                label: qualification
                              }))
                            )
                            .find((opt) => opt.label === label)
                          return match || { label }
                        })}
                        onChange={(e, value) => {
                          setFieldValue(
                            "drivingLicensQualifications",
                            value.map((v) => v.label)
                          )
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label={t(
                              "settings.user.drivingLicensQualifications",
                              "Driving License Qualifications"
                            )}
                            onBlur={handleBlur}
                            error={
                              touched.drivingLicensQualifications && errors.drivingLicensQualifications
                                ? true
                                : false
                            }
                            helperText={
                              touched.drivingLicensQualifications && errors.drivingLicensQualifications
                                ? errors.drivingLicensQualifications
                                : null
                            }
                          />
                        )}
                      />
                      <StandardTextBox
                        text={t(
                          `settings.user.bky_license_pick_highest`,
                          `Always add the driver's highest driving license authorization for the BKY check to work correctly.`
                        )}
                        icon={faInfoCircle}
                        color={theme.palette.default.main}
                        bgcolor={theme.palette.primaryLight.main}
                        fontSize={14}
                        marginBottom={0}
                      />
                    </Grid>
                  </Grid>
                </PaperBox>
              )}

              {(findFeatureInModules(values?.modules, "Planning")?.enabled ||
                (userTemplateResult.status === 2 &&
                  userTemplateResult.data
                    .filter((template) => values?.UserRoleTemplateIds?.includes(template.id))
                    .some((template) =>
                      template.roles.some((role) => role.role.startsWith("Planning"))
                    ))) && (
                <BplUserInfo
                  values={values}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  touched={touched}
                  errors={errors}
                  bplOptionsResult={bplOptionsResult}
                  bplUserResult={bplUserResult}
                  parentIsSysAdmin={parentIsSysAdmin}
                  customer={customer}
                />
              )}
            </Grid>
          </Grid>

          <Box marginTop={15}></Box>
          <Box sx={{ position: "fixed", bottom: 8, left: 0, right: 15 }} elevation={3}>
            <BottomNavigation
              sx={{ justifyContent: "flex-end", backgroundColor: "transparent", height: "auto" }}>
              <Button color="primary" type="submit" result={result} feedback={feedback}>
                {!addUser ? t("global.save", "Save") : t("global.add", "Add")}
              </Button>
            </BottomNavigation>
          </Box>
        </form>
      )}
    </Formik>
  ) : null
}
